import React from 'react';
import { cx } from './cx';
import { Spinner } from './Spinner';

const variants = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 disabled:bg-brand-300',
  secondary: 'bg-surface text-ink border border-line hover:bg-chip disabled:text-muted',
  ghost: 'text-brand-700 hover:bg-brand-50 disabled:text-muted',
  danger: 'bg-danger text-white hover:opacity-90 disabled:opacity-50',
};
const sizes = { md: 'px-4 py-2 text-sm', sm: 'px-2.5 py-1.5 text-xs' };

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
  loading?: boolean;
}

/**
 * `loading` disables the button and shows a spinner, which also prevents duplicate submissions.
 * Defaults to type="button" so it never submits a form by accident.
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, disabled, className, type = 'button', children, ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-colors disabled:cursor-not-allowed',
        variants[variant], sizes[size], className
      )}
      {...rest}
    >
      {loading && <Spinner className="h-4 w-4" />}
      {children}
    </button>
  );
});
